/**
 * anomalySeverity — the ONE source of truth for how anomaly severity maps to
 * color across every surface that lists anomalies (DS wizard AnomalyBanner +
 * AnomalyResolver, the legacy CleanupValidation table, the accountant
 * AnomalyReviewContent). Previously each surface hard-coded its own map and they
 * drifted (some orange, some red), so the same "HIGH shows orange" bug got
 * re-fixed per-dashboard. Import this instead of writing a new severity→color map.
 *
 * Design law (K3 ruling 2026-08-23, DESIGN_SYSTEM):
 *  - The anomaly card/row is STRUCTURE → always NEUTRAL. It carries no severity wash.
 *  - Severity is communicated by the CHIP only. Severity is CRIMSON's channel
 *    (`--color-blocker`) — orange (`--color-brand`) may NOT carry severity; orange
 *    is reserved for the primary action.
 *  - critical/high → blocker red (outline chip). medium/low/unknown → muted neutral.
 */
import type { CSSProperties } from 'react';

export type AnomalySeverity = 'low' | 'medium' | 'high' | 'critical' | string | undefined | null;

export interface AnomalySeverityStyle {
  /** true for critical|high — the blocker-level severities that read red. */
  isBlocker: boolean;
  /** Neutral card/row surface — identical across severities (card is structure). */
  card: { background: string; borderColor: string };
  /** Severity chip — crimson outline for blocker, muted outline otherwise. */
  chip: CSSProperties;
}

export function isBlockerSeverity(severity: AnomalySeverity): boolean {
  return severity === 'critical' || severity === 'high';
}

export function anomalySeverityStyle(severity: AnomalySeverity): AnomalySeverityStyle {
  const isBlocker = isBlockerSeverity(severity);
  return {
    isBlocker,
    card: {
      background: 'var(--color-bg-elevated)',
      borderColor: 'var(--color-border)',
    },
    chip: isBlocker
      ? {
          background: 'transparent',
          color: 'var(--color-blocker)',
          boxShadow: 'inset 0 0 0 1px var(--color-blocker)',
        }
      : {
          background: 'transparent',
          color: 'var(--color-text-muted)',
          boxShadow: 'inset 0 0 0 1px var(--color-border)',
        },
  };
}
